import React from 'react';

import { Button, Divider, Group, LoadingOverlay, Paper, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { GymRetrieve } from 'types';
import { STRIPE_TERMINAL_S700_URL } from 'utils/external-resources';

import StripeTerminalReaderDeleteModal from './StripeTerminalReaderDeleteModal';
import StripeTerminalReaderInstructionsModal from './StripeTerminalReaderInstructionsModal';
import StripeTerminalReaderListItem from './StripeTerminalReaderListItem';
import StripeTerminalReaderRegisterModal from './StripeTerminalReaderRegisterModal';

const StripeTerminalReaderCard = ({
  gymData,
  gymDataIsLoading,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
}): JSX.Element => {
  const stripeTerminalReader = gymData?.stripeTerminalReader ?? null;

  const { trackCardReaderPurchaseClicked } = useStaffTracking();

  const [isInstructionsModalOpened, { open: openInstructionsModal, close: closeInstructionsModal }] =
    useDisclosure(false);
  const [isRegisterModalOpened, { open: openRegisterModal, close: closeRegisterModal }] =
    useDisclosure(false);
  const [isDeleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const handleOpenRegisterFromInstructions = (): void => {
    closeInstructionsModal();
    openRegisterModal();
  };

  return (
    <>
      <StripeTerminalReaderInstructionsModal
        opened={isInstructionsModalOpened}
        onClose={closeInstructionsModal}
        onRegisterClick={handleOpenRegisterFromInstructions}
      />
      {!stripeTerminalReader && (
        <StripeTerminalReaderRegisterModal opened={isRegisterModalOpened} onClose={closeRegisterModal} />
      )}
      {stripeTerminalReader && (
        <StripeTerminalReaderDeleteModal
          opened={isDeleteModalOpened}
          onClose={closeDeleteModal}
          stripeTerminalReader={stripeTerminalReader}
        />
      )}

      <Paper shadow='xl' p='md' pb='lg' sx={{ position: 'relative' }}>
        <LoadingOverlay visible={gymDataIsLoading} />
        <Group position='apart'>
          <Title order={4} mb='sm'>
            In Person Payments
          </Title>

          <Text mb='sm' color='dimmed' sx={{ cursor: 'pointer' }} underline onClick={openInstructionsModal}>
            How to?
          </Text>
        </Group>

        {stripeTerminalReader ? (
          <Stack spacing='sm'>
            <Text>Your Readers</Text>
            <StripeTerminalReaderListItem
              stripeTerminalReader={stripeTerminalReader}
              onDeleteClick={openDeleteModal}
            />
          </Stack>
        ) : (
          <Stack spacing='md'>
            <Text>Accept tap-to-pay card payments instantly with a Stripe card reader.</Text>
            <Text
              variant='outline'
              underline
              color='kiloBlue'
              size='md'
              fw='bold'
              component='a'
              href={STRIPE_TERMINAL_S700_URL}
              target='_blank'
              onClick={() => trackCardReaderPurchaseClicked()}
            >
              Purchase Card Reader
            </Text>

            <Divider mx='-sm' />

            <Text>Already received the card reader?</Text>
            <Group>
              <Button onClick={openRegisterModal}>Register Card Reader</Button>
            </Group>
          </Stack>
        )}
      </Paper>
    </>
  );
};

export default StripeTerminalReaderCard;
